import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Download, X, FileWarning } from "lucide-react";
import PdfViewer from "@/components/dashboard/PdfViewer";
import PhotoViewer from "@/components/dashboard/PhotoViewer";

const FilePreview = ({ file, onClose, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    let revoked = null;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const meta = await api.get(`/files/${file.file_id}/preview`);
        if (cancelled) return;
        setData(meta.data);
        if (meta.data.type === "image" || meta.data.type === "pdf") {
          const res = await api.get(`/files/${file.file_id}/download`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          revoked = url;
          setBlobUrl(url);
        }
      } catch {
        setData({ type: "error", message: "Could not load preview" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [file]);

  const body = () => {
    if (loading) return <div className="flex h-full items-center justify-center text-sm text-gray-400">Loading preview…</div>;
    if (!data) return null;
    switch (data.type) {
      case "image":
        return <PhotoViewer file={file} blobUrl={blobUrl} />;
      case "pdf":
        return <PdfViewer file={file} blobUrl={blobUrl} onSaved={() => { /* file replaced */ }} />;
      case "text":
        return (
          <pre className="h-full overflow-auto whitespace-pre-wrap bg-white p-6 font-mono text-sm text-gray-800" data-testid="preview-text">
            {data.text}
          </pre>
        );
      case "html":
        return (
          <div className="h-full overflow-auto bg-white p-8" data-testid="preview-doc">
            <div className="prose prose-sm mx-auto max-w-3xl" dangerouslySetInnerHTML={{ __html: data.html }} />
          </div>
        );
      case "sheet": {
        const sheet = data.sheets[activeSheet];
        return (
          <div className="flex h-full flex-col bg-white" data-testid="preview-sheet">
            {data.sheets.length > 1 && (
              <div className="flex gap-1 overflow-x-auto border-b border-gray-200 px-3 py-2">
                {data.sheets.map((s, i) => (
                  <button key={s.name} onClick={() => setActiveSheet(i)} className={`whitespace-nowrap rounded px-3 py-1 text-xs ${i === activeSheet ? "bg-[#0364b8] text-white" : "bg-gray-100 text-gray-600"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-auto p-3">
              <table className="border-collapse text-xs">
                <tbody>
                  {sheet.rows.map((row, ri) => (
                    <tr key={ri}>
                      <td className="sticky left-0 border border-gray-200 bg-gray-50 px-2 py-1 text-gray-400">{ri + 1}</td>
                      {row.map((cell, ci) => (
                        <td key={ci} className="border border-gray-200 px-2 py-1 text-gray-700">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {sheet.rows.length === 0 && <p className="p-6 text-sm text-gray-400">Empty sheet</p>}
            </div>
          </div>
        );
      }
      default:
        return (
          <div className="flex h-full flex-col items-center justify-center gap-3 bg-white p-8 text-center" data-testid="preview-unsupported">
            <FileWarning className="h-10 w-10 text-gray-300" />
            <p className="text-sm text-gray-500">{data.message || "No in-app preview available for this file type."}</p>
            <button onClick={() => onDownload(file)} className="rounded-md bg-[#0364b8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0358a0]">
              Download instead
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/70 backdrop-blur-sm" data-testid="file-preview" onClick={onClose}>
      <div className="flex shrink-0 items-center justify-between bg-[#1a1a1a] px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="truncate text-sm font-medium">{file.name}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => onDownload(file)} data-testid="preview-download-btn" className="flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-1.5 text-xs hover:bg-white/10">
            <Download className="h-3.5 w-3.5" /> Download
          </button>
          <button onClick={onClose} data-testid="preview-close-btn" aria-label="Close" className="rounded-md p-1.5 hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {body()}
      </div>
    </div>
  );
};

export default FilePreview;
